import React, { useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

interface InputProps {
  id: string;
  label: string;
  type?: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  autoComplete?: string;
  showPasswordToggle?: boolean;
  onTogglePassword?: () => void;
  srOnlyLabel?: boolean; // New prop for screen-reader-only labels
  formatPhone?: boolean; // New prop for phone number formatting
}

const Input: React.FC<InputProps> = ({ 
  id, 
  label, 
  type = 'text', 
  value, 
  onChange, 
  placeholder, 
  required = false,
  autoComplete,
  showPasswordToggle = false,
  onTogglePassword,
  srOnlyLabel = false,
  formatPhone = false
}) => {
  const [internalShowPassword, setInternalShowPassword] = useState(false);
  
  const isPasswordField = type === 'password' || showPasswordToggle;
  const actualType = isPasswordField && internalShowPassword ? 'text' : type;
  
  // Phone number formatting function
  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // Limit to 10 digits
    const limited = cleaned.substring(0, 10);
    
    // Apply formatting based on length
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 6) {
      return `(${limited.slice(0, 3)}) ${limited.slice(3)}`;
    } else {
      return `(${limited.slice(0, 3)}) ${limited.slice(3, 6)}-${limited.slice(6)}`;
    }
  };
  
  const handleInputChange = (inputValue: string) => {
    if (formatPhone) {
      const formattedValue = formatPhoneNumber(inputValue);
      onChange(formattedValue);
    } else {
      onChange(inputValue);
    }
  };
  
  // Format the initial value when component mounts or value changes
  useEffect(() => {
    if (formatPhone && value && value !== formatPhoneNumber(value)) {
      const formattedValue = formatPhoneNumber(value);
      onChange(formattedValue);
    }
  }, [value, formatPhone, onChange]);
  
  const handleTogglePassword = () => {
    if (onTogglePassword) {
      onTogglePassword();
    } else {
      setInternalShowPassword(!internalShowPassword);
    }
  };

  return (
    <div className="mb-4">
      <label 
        htmlFor={id} 
        className={srOnlyLabel ? "sr-only" : "block text-sm font-medium mb-2"} 
        style={!srOnlyLabel ? { color: '#002457' } : {}}
      >
        {label} {required && !srOnlyLabel && <span className="text-red-500">*</span>}
      </label>
      <div className="relative">
        <input
          id={id}
          type={actualType}
          value={value}
          onChange={(e) => handleInputChange(e.target.value)}
          placeholder={placeholder}
          className={`border-2 border-gray-200 p-4 rounded-2xl w-full focus:border-blue-500 focus:outline-none transition-colors duration-200 ${isPasswordField ? 'pr-12' : ''}`}
          autoComplete={autoComplete}
          required={required}
        />
        {isPasswordField && (
          <button
            type="button"
            onClick={handleTogglePassword}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            {(onTogglePassword ? type === 'password' : !internalShowPassword) ? (
              <EyeIcon className="h-5 w-5" />
            ) : (
              <EyeSlashIcon className="h-5 w-5" />
            )}
          </button>
        )}
      </div>
    </div>
  );
};

export default Input;
